import React, {useEffect, useState} from 'react';
import {DateRangePicker} from 'react-date-range';
import {useLocation, useNavigate} from 'react-router';
import moment from 'moment';
import {enGB} from 'date-fns/locale';
import {Popper} from '@mui/material';
import {DateInput} from 'rsuite';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {limit, SaveParams} from 'utils';
import {Svg} from 'assets';

export const DateSelectorRange = ({
                                      handleGetDates,
                                      filters,
                                      startName,
                                      endName,
                                      reset,
                                      storageSave,
                                  }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const info = filters ? filters : location?.state;
    const open = Boolean(anchorEl);

    const [state, setState] = useState([{
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
    }]);

    const customLocale = {
        ...enGB,
        options: {...enGB.options, weekStartsOn: 1},
    };

    useEffect(() => {
        if (info?.[startName] || info?.[endName]) {
            const params = {}

            info[startName] ? params[startName] = moment(info[startName]).format('YYYY-MM-DDT00:00:01') : delete params[startName];
            info[endName] ? params[endName] = moment(info[endName]).format('YYYY-MM-DDT23:59:59') : delete params[endName];


            setState([{
                startDate: info?.[startName] ? moment(info[startName]).toDate() : new Date(),
                endDate: info?.[endName] ? moment(info[endName]).toDate() : new Date(),
                key: 'selection',
            }]);

            handleGetDates?.(params);
        }
    }, [info, startName, endName]);


    const handleDateChange = (item) => {
        if (!item?.selection) return;
        const {startDate, endDate} = item.selection;

        const stateParams = {
            startDate,
            endDate,
            [startName]: moment(startDate).format('YYYY-MM-DDT00:00:01'),
            [endName]: moment(endDate).format('YYYY-MM-DDT23:59:59'),
            key: 'selection',
        }

        setState([stateParams]);

        const params = {...info};
        params[startName] = moment(startDate).format('YYYY-MM-DDT00:00:01');
        params[endName] = moment(endDate).format('YYYY-MM-DDT23:59:59');

        params.page = 1;
        params.limit = limit;
        params.skip = 0;

        if (handleGetDates) {
            handleGetDates(params)
        } else {
            if (storageSave) storageSave(params);
            SaveParams(location?.pathname, navigate, params);
        }
    };

    const handleClear = () => {
        const today = new Date();
        setState([{startDate: today, endDate: today, key: 'selection'}]);

        const params = {...info};
        delete params[startName];
        delete params[endName];

        params.page = 1;
        params.limit = limit;
        params.skip = 0;

        if (handleGetDates) {
            handleGetDates(params)
        } else {
            if (storageSave) storageSave(params);
            SaveParams(location?.pathname, navigate, params);
        }
    };

    const handleClick = (event) => setAnchorEl(anchorEl ? null : event.currentTarget);

    const handleTypeDate = (value, name, originalName) => {
        if (!(value instanceof Date) || isNaN(value.getTime())) return;
        const params = {}
        if (originalName === 'startDate') params[name] = moment.utc(value).format('YYYY-MM-DDT00:00:01');
        if (originalName === 'endDate') params[name] = moment.utc(value).format('YYYY-MM-DDT23:59:59');

        setState(prevState => {
            const current = prevState[0];
            return [{
                ...current,
                [originalName]: value,
                ...params,
            }];
        });
    };

    const handleBlur = (name, originalName) => {
        const value = state[0][name];
        const formattedValue = value && moment(value).format('YYYY-MM-DD');
        const existingValue = info?.[name] ? moment(info[name]).format('YYYY-MM-DD') : null;

        if (formattedValue && formattedValue !== existingValue) {
            const params = {
                ...info,
            };
            if (originalName === 'startDate') params[name] = moment.utc(value).format('YYYY-MM-DDT00:00:01');
            if (originalName === 'endDate') params[name] = moment.utc(value).format('YYYY-MM-DDT23:59:59');

            const otherName = name === startName ? endName : startName;
            const otherDate = state[0][otherName];

            if (otherDate) params[otherName] = moment(otherDate).format('YYYY-MM-DD');

            params.page = 1;
            params.limit = limit;
            params.skip = 0;

            if (handleGetDates) {
                handleGetDates(params)
            } else {
                if (storageSave) storageSave(params);
                SaveParams(location?.pathname, navigate, params);
            }
        }
    };

    const shiftDateRange = (direction = 'next') => {
        const start = state[0]?.[startName] || info?.[startName];
        const end = state[0]?.[endName] || info?.[endName];
        if (!start || !end) return;

        const date1 = moment(start);
        const date2 = moment(end);
        const days = date2.diff(date1, 'days') + 1;
        const shift = direction === 'next' ? days : -days;

        const updatedStart = date1.clone().add(shift, 'days').toDate();
        const updatedEnd = date2.clone().add(shift, 'days').toDate();

        setState([{
            startDate: updatedStart,
            endDate: updatedEnd,
            [startName]: moment(updatedStart).format('YYYY-MM-DDT00:00:01'),
            [endName]: moment(updatedEnd).format('YYYY-MM-DDT23:59:59'),
            key: 'selection'
        }]);

        if (handleGetDates) {
            handleGetDates({
                ...info,
                [startName]: moment(updatedStart).format('YYYY-MM-DDT00:00:01'),
                [endName]: moment(updatedEnd).format('YYYY-MM-DDT23:59:59'),
                page: 1,
                limit: limit,
                skip: 0,
            })
        } else {
            SaveParams(location?.pathname, navigate, {
                ...info,
                [startName]: moment(updatedStart).format('YYYY-MM-DDT00:00:01'),
                [endName]: moment(updatedEnd).format('YYYY-MM-DDT23:59:59'),
                page: 1,
                limit: limit,
                skip: 0,
            });
        }
    };

    const navigateToday = () => {
        const today = new Date();
        const todayStart = new Date(today.setHours(0, 0, 1));
        const todayEnd = new Date(today.setHours(23, 59, 59));

        setState([{
            startDate: todayStart,
            endDate: todayEnd,
            [startName]: moment(todayStart).format('YYYY-MM-DDT00:00:01'),
            [endName]: moment(todayEnd).format('YYYY-MM-DDT23:59:59'),
            key: 'selection'
        }]);

        const saveParams = {
            ...info,
            [startName]: moment(todayStart).format('YYYY-MM-DDT00:00:01'),
            [endName]: moment(todayEnd).format('YYYY-MM-DDT23:59:59'),
            page: 1,
            limit: limit,
            skip: 0,
        }

        if (handleGetDates) {
            handleGetDates(saveParams)
        } else {
            SaveParams(location?.pathname, navigate, saveParams);
        }
    };

    return (
        <div className="date-selector-range-wrapper">
            <div className="date-selector-range-box">
                <div className="date-selector-range-inputs-wrapper" onClick={e => e.stopPropagation()}>
                    {(state[0]?.[startName] && state[0]?.[endName]) &&
                        <button className="date-selector-range-arrows" onClick={() => shiftDateRange('prev')}>
                            <Svg.ArrowRightSvg style={{transform: 'rotate(-180deg)'}}/>
                        </button>
                    }

                    <button
                        className="reset-btn"
                        onClick={handleClick}
                        style={{display: 'flex'}}
                    >
                        <Svg.CalendarSvg
                            style={{filter: 'brightness(0) saturate(100%) invert(71%) sepia(4%) saturate(2851%) hue-rotate(160deg) brightness(103%) contrast(93%)'}}/>
                    </button>

                    <div className='align-center' style={{gap: '4px'}}>
                        <DateInput
                            placeholder="mm/dd/yyyy"
                            format="MM/dd/yyyy"
                            value={state[0]?.[startName] ? moment.utc(state[0][startName]).toDate() : null}
                            onChange={date => {
                                if (date && date <= new Date(2045, 11, 31)) handleTypeDate(date, startName, 'startDate');
                            }}
                            onBlur={() => handleBlur(startName, 'startDate')}
                        />
                        <p>-</p>
                        <DateInput
                            placeholder="mm/dd/yyyy"
                            format="MM/dd/yyyy"
                            value={state[0]?.[endName] ? moment.utc(state[0][endName]).toDate() : null}
                            onChange={date => {
                                if (date && date <= new Date(2045, 11, 31)) handleTypeDate(date, endName, 'endDate');
                            }}
                            onBlur={() => handleBlur(endName, 'endDate')}
                        />
                    </div>

                    {(state[0]?.[startName] || state[0]?.[endName]) && (
                        <button className="date-selector-range-arrows" onClick={handleClear}>
                            <Svg.CloseSvg/>
                        </button>
                    )}

                    {(state[0]?.[startName] && state[0]?.[endName]) &&
                        <button className="date-selector-range-arrows" onClick={() => shiftDateRange('next')}>
                            <Svg.ArrowRightSvg/>
                        </button>
                    }
                </div>

                <button className="date-selector-range-today-btn" onClick={navigateToday}>
                    Today
                </button>
            </div>

            <Popper
                open={open} anchorEl={anchorEl} placement="bottom-start" style={{zIndex: 9999}}>
                <DateRangePicker
                    ranges={state}
                    onChange={handleDateChange}
                    showDateDisplay={false}
                    locale={customLocale}
                    rangeColors={['#88c3e1']}
                />
                <button className="clear-range-bnt" onClick={handleClear}>Clear</button>
            </Popper>

            {open && <div className="beck-drop" onClick={() => setAnchorEl(null)}/>}
        </div>
    );
};
